import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { compose, setPropTypes, withState, withHandlers } from 'recompose';
import { withRouter } from 'react-router';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import { Table, TableBody, TableCell, TableRow } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import Typography from '@material-ui/core/Typography';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import classNames from 'classnames';
import CancelButton from '../../components/atoms/CancelButton';
import ConfirmButton from '../../components/atoms/ConfirmButton';
import {
  getIdentities,
  editIdentityUserKidStatus
} from '../../actions/Identity';
import { sendLogin } from '../../auth/Auth';

const styles = {
  root: {
    position: 'absolute',
    width: '70%',
    height: 'auto',
    maxHeight: '80%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff',
    padding: 88,
    overflow: 'scroll',
    outline: 'none'
  },
  itemDiv: {
    marginBottom: 16
  },
  titleHeader: {
    fontWeight: 700,
    fontSize: 24,
    lineHeight: '140%'
  },
  textUserName: {
    fontWeight: 700,
    fontSize: 20,
    lineHeight: '140%',
    marginTop: 40
  },
  fs20: {
    fontWeight: 700,
    fontSize: 20,
    lineHeight: '140%'
  },
  fs16: {
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '100%'
  },
  mb52: {
    marginBottom: 52
  },
  reasonComment: {
    width: '100%',
    height: '200px',
    maxWidth: '100%'
  },
  kidInfoContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 48,
    marginBottom: 32
  },
  kidInfoDetails: {
    flexShrink: 0,
    width: 500,
    marginRight: 20
  },
  receiptDetail: {
    display: 'flex',
    marginTop: 8
  },
  receiptInfo: {
    width: 400,
    marginLeft: 32
  },
  receiptRow: {
    borderBottom: '1px solid #E9EAEB',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 16px 8px 24px'
  },
  receiptImage: {
    width: 377
  },
  titleRow: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '18px',
    minWidth: 100
  },
  dateTextRow: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 13,
    fontWeight: 400,
    lineHeight: '15px'
  },
  dateText: {
    fontSize: 20,
    fontWeight: 700,
    margin: '0 2px',
    lineHeight: '23px'
  },
  shopTextRow: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: '18px'
  },
  textRow: {
    fontSize: 24,
    fontWeight: 700,
    lineHeight: '28px'
  },
  yen: {
    fontSize: 12,
    fontWeight: 400,
    marginLeft: 2,
    lineHeight: '13px'
  },
  receiptListItem: {
    marginTop: 32
  },
  titleReceiptListItem: {
    color: '#808080',
    fontSize: 18,
    fontWeight: 700,
    marginLeft: 10,
    lineHeight: '25px'
  },
  warningIcon: {
    color: '#F9BB00'
  },
  priceColumn: {
    textAlign: 'center'
  },
  justifyContentEnd: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  label: {
    backgroundColor: '#F50357',
    color: '#FFFFFF',
    height: 'fit-content',
    textAlign: 'center',
    padding: '3px 10px',
    fontSize: 12,
    fontWeight: 700,
    marginRight: 16
  },
  headerRadioBox: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '16px',
    marginBottom: 8
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      name: state.loginInfo.name
    }),
    dispatch => ({
      actions: bindActionCreators(
        { getIdentities, editIdentityUserKidStatus },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('total', 'setTotal', 0),
  withState('subTotal', 'setSubTotal', 0),
  withState('tax8PercentTotal', 'setTax8PercentTotal', 0),
  withState('tax10PercentTotal', 'setTax10PercentTotal', 0),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    }
  })
);

const Main = enhance(
  ({
    classes,
    receipt,
    total,
    setTotal,
    subTotal,
    setSubTotal,
    tax8PercentTotal,
    setTax8PercentTotal,
    tax10PercentTotal,
    setTax10PercentTotal,
    statusReceiptSelected,
    setStatusReceiptSelected,
    confirmAction,
    cancelAction
  }) => {
    // With all items that have taxed = true
    function withAllItemsTaxed(arrayNumber) {
      let sumTotal = 0;
      let sumItemTax8 = 0;
      let sumItemTax10 = 0;

      if (arrayNumber && arrayNumber.length > 0) {
        // eslint-disable-next-line array-callback-return
        arrayNumber.map(row => {
          if (row.taxed === true) {
            // Total of items that included tax
            sumTotal += row.amount ? row.amount : 0;

            if (row.taxRate === 0.08) {
              // Sum price of items having tax = 8%
              sumItemTax8 += row.amount ? row.amount : 0;
            }

            if (row.taxRate === 0.1) {
              // Sum price of items having tax = 10%
              sumItemTax10 += row.amount ? row.amount : 0;
            }
          }
        });
      }
      return { sumTotal, sumItemTax8, sumItemTax10 };
    }

    // With items that have taxed = false
    function withAllItemsNotTaxed(arrayNumber) {
      let sumSubTotal = 0;
      let sumItemTax8 = 0;
      let sumItemTax10 = 0;

      if (arrayNumber && arrayNumber.length > 0) {
        // eslint-disable-next-line array-callback-return
        arrayNumber.map(row => {
          if (row.taxed === false) {
            // Sum of all subtotal
            sumSubTotal += row.amount ? row.amount : 0;

            // Sum price of items having tax = 8%
            if (row.taxRate === 0.08) {
              sumItemTax8 += row.amount ? row.amount : 0;
            }

            // Sum of all items have tax = 10%
            if (row.taxRate === 0.1) {
              sumItemTax10 += row.amount ? row.amount : 0;
            }
          }
        });
      }
      return {
        sumSubTotal,
        sumItemTax8,
        sumItemTax10
      };
    }

    function sumAllTotal(arrayNumber) {
      // Calulate taxMount, total, and subtotal of all items that were not included tax
      const sumNotTaxed = withAllItemsNotTaxed(arrayNumber);
      let taxAmount8NotTaxed = 0;
      let taxAmount10NotTaxed = 0;
      let sumSubTotalNotTaxed = 0;
      let sumTotalNotTaxed = 0;
      if (sumNotTaxed) {
        taxAmount8NotTaxed = Math.floor(sumNotTaxed.sumItemTax8 * 0.08);
        taxAmount10NotTaxed = Math.floor(sumNotTaxed.sumItemTax10 * 0.1);
        sumSubTotalNotTaxed = sumNotTaxed.sumSubTotal;

        sumTotalNotTaxed =
          sumSubTotalNotTaxed + taxAmount8NotTaxed + taxAmount10NotTaxed;
      }

      // Calulate taxMount, total, and subtotal of all items that were included tax
      const sumTaxed = withAllItemsTaxed(arrayNumber);
      let taxAmount8Taxed = 0;
      let taxAmount10Taxed = 0;
      let sumSubTotalTaxed = 0;
      let sumTotalTaxed = 0;
      if (sumTaxed) {
        // Công thức ban đầu là Math.floor(((sumTaxed.sumItemTax8) / 1.08) * 0.08);
        // nhưng do phép tính "sumTaxed.sumItemTax8) / 1.08" bị lỗi phép chia với phân số (ví dụ 1080/1.08 đúng ra phải bằng 1000 nhưng máy tính tính ra thành 999.999999999)
        // nên để tránh lỗi trên thì sẽ nhân cả tử và mẫu vs 100 để k phải làm phép tính chia cho số thập phân. Sửa tương tự vs taxAmount10Taxed
        // See more: https://stackoverflow.com/questions/15383376/javascript-division-giving-wrong-answer
        taxAmount8Taxed = Math.floor(
          ((sumTaxed.sumItemTax8 * 100) / 108) * 0.08
        );
        taxAmount10Taxed = Math.floor(
          ((sumTaxed.sumItemTax10 * 10) / 11) * 0.1
        );
        sumTotalTaxed = sumTaxed.sumTotal;

        sumSubTotalTaxed = sumTotalTaxed - taxAmount8Taxed - taxAmount10Taxed;
      }

      setTotal(sumTotalNotTaxed + sumTotalTaxed);
      setSubTotal(sumSubTotalNotTaxed + sumSubTotalTaxed);
      setTax8PercentTotal(taxAmount8NotTaxed + taxAmount8Taxed);
      setTax10PercentTotal(taxAmount10NotTaxed + taxAmount10Taxed);
    }

    useEffect(() => {
      sumAllTotal(receipt.receiptDetails);
    }, []);

    const changeStatus = value => {
      setStatusReceiptSelected(value);
    };

    return (
      <div className={classes.root}>
        <Typography variant="h6" className={classes.titleHeader}>
          承認&レシート詳細
        </Typography>
        <div className={classes.textUserName}>
          <Typography variant="p" color="textPrimary" className={classes.fs20}>
            {receipt.userId}
          </Typography>
        </div>
        <div>
          <Typography variant="p" color="textPrimary" className={classes.fs20}>
            {`${receipt.userName} 様`}
          </Typography>
        </div>
        <div className={classes.receiptDetail}>
          <div>
            <img
              alt="receiptImage"
              src={receipt.receiptImage}
              className={classes.receiptImage}
            />
          </div>
          <div className={classes.receiptInfo}>
            <div>
              <div className={classes.receiptRow}>
                <div className={classes.titleRow}>日付</div>
                <div className={classes.dateTextRow}>
                  {receipt.receiptDate && (
                    <>
                      <span className={classes.dateText}>
                        {moment(receipt.receiptDate).year()}
                      </span>
                      <span>年</span>
                      <span className={classes.dateText}>
                        {String(
                          moment(receipt.receiptDate).month() + 1
                        ).padStart(2, '0')}
                      </span>
                      <span>月</span>
                      <span className={classes.dateText}>
                        {String(moment(receipt.receiptDate).date()).padStart(
                          2,
                          '0'
                        )}
                      </span>
                      <span>日</span>
                    </>
                  )}
                </div>
              </div>
              <div className={classes.receiptRow}>
                <div className={classes.titleRow}>店舗</div>
                <div className={classes.shopTextRow}>
                  {receipt.receiptShopName ? `${receipt.receiptShopName} ` : ''}
                  {receipt.receiptBranch ? receipt.receiptBranch : ''}
                </div>
              </div>
              <div className={classes.receiptRow}>
                <div className={classes.titleRow}>合計</div>
                <div className={classes.textRow}>
                  {total ? total.toLocaleString() : 0}
                  <span className={classes.yen}>円</span>
                </div>
              </div>
              <div className={classes.receiptRow}>
                <div className={classes.titleRow}>小計</div>
                <div className={classes.textRow}>
                  {subTotal && subTotal.toLocaleString()}
                  <span className={classes.yen}>円</span>
                </div>
              </div>
              <div className={classes.receiptRow}>
                <div className={classes.titleRow}>消費税(8%)</div>
                <div className={classes.textRow}>
                  {tax8PercentTotal && tax8PercentTotal.toLocaleString()}
                  <span className={classes.yen}>円</span>
                </div>
              </div>
              <div className={classes.receiptRow}>
                <div className={classes.titleRow}>消費税(10%)</div>
                <div className={classes.textRow}>
                  {tax10PercentTotal && tax10PercentTotal.toLocaleString()}
                  <span className={classes.yen}>円</span>
                </div>
              </div>
            </div>
            <div className={classes.receiptListItem}>
              <div className={classes.titleReceiptListItem}>品目</div>
              <Table className={classes.receiptTable}>
                <TableBody>
                  {receipt.receiptDetails &&
                    receipt.receiptDetails.map(row => {
                      let taxText = '';
                      if (row.taxRate === 0) {
                        taxText = '非抜';
                      } else if (row.taxed) {
                        taxText = '税込';
                      } else {
                        taxText = '税抜';
                      }

                      return (
                        <TableRow key={row.id}>
                          <TableCell className={classes.shopTextRow}>
                            {row.name}
                          </TableCell>
                          <TableCell>
                            {row.shopFoodFlag === 0 && (
                              <WarningIcon className={classes.warningIcon} />
                            )}
                          </TableCell>
                          <TableCell className={classes.priceColumn}>
                            <div className={classes.justifyContentEnd}>
                              <div>
                                <div className={classes.textRow}>
                                  <span>{row.amount.toLocaleString()}</span>
                                  <span className={classes.yen}>円</span>
                                </div>
                                <div className={classes.yen}>
                                  {`（${taxText}）`}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
        <div className={classNames(classes.itemDiv, classes.kidInfoContainer)}>
          <div className={classes.label}>必須</div>
          <div className={classes.kidInfoDetails}>
            <React.Fragment>
              <div className={classes.headerRadioBox}>申請結果</div>
              <RadioGroup
                value={statusReceiptSelected}
                onChange={e => {
                  const newStatus = parseInt(e.target.value, 10);
                  changeStatus(newStatus);
                }}
                row
              >
                <FormControlLabel
                  value={3}
                  control={<Radio color="primary" />}
                  label="承認待ち"
                  disabled={receipt.status === 1}
                  className={classes.fs16}
                />
                <FormControlLabel
                  value={1}
                  control={<Radio color="primary" />}
                  label="承認済み"
                  disabled={receipt.status === 1}
                  className={classes.fs16}
                />
                <FormControlLabel
                  value={2}
                  control={<Radio color="primary" />}
                  label="承認保留"
                  disabled={receipt.status === 1}
                  className={classes.fs16}
                />
                <FormControlLabel
                  value={4}
                  control={<Radio color="primary" />}
                  label="非承認"
                  disabled={receipt.status === 1}
                  className={classes.fs16}
                />
              </RadioGroup>
            </React.Fragment>
          </div>
        </div>
        <div>
          <CancelButton onClickAction={cancelAction} color="primary" />
          <ConfirmButton
            consent={receipt.status !== 1}
            onClickAction={confirmAction}
          />
        </div>
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
